/**
 * Import Matter Modal
 *
 * Dialog for importing a single matter from the firm's PMS.
 * Flow: look up → preview (+ the options that PMS can't supply) → import → poll status → redirect.
 *
 * One component for every provider: the backend's import routes are provider-parameterised, so the
 * flow is genuinely the same and only its *inputs* differ. Everything that differs is declared in
 * `import-provider-config.ts` — the lookup modes a provider's search really supports, and the facts
 * (Vale matter type, state, deceased person) it cannot tell Vale about a matter.
 */

'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Building2,
  ChevronRight
} from 'lucide-react';
import Image from 'next/image';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Banner } from '@/components/ui/banner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ACCOUNT_ROUTES, MATTER_ROUTES } from '@/lib/constants/routes';
import {
  IMPORT_SINGLE_POLL_INTERVAL_MS,
  getPmsImportSingleStatus,
  importSinglePmsMatter,
  lookupPmsMatterByReference,
  lookupPmsMattersByClientName
} from '@/lib/services/integrations/pms';
import { isIntegrationReauthRequiredError } from '@/lib/services/integrations/pms/integration-auth-error';
import type {
  MatterLookupResult,
  PmsProvider
} from '@/lib/services/integrations/pms';
import {
  EMPTY_IMPORT_OPTIONS,
  ImportOptions,
  areImportOptionsComplete,
  type ImportOptionsValue
} from './import-options';
import {
  getPmsImportProviderConfig,
  type PmsImportProviderConfig,
  type PmsLookupMode
} from './import-provider-config';

type ImportPhase =
  | { phase: 'form' }
  | { phase: 'results'; matters: MatterLookupResult[] }
  | { phase: 'preview'; matter: MatterLookupResult }
  | { phase: 'importing'; externalMatterId: string }
  | { phase: 'success'; valeMatterId: number }
  /**
   * `declined` and `failed` are different things and must not read alike. A declined import is a 200
   * saying the matter or the firm's configuration is missing something — the message names what to
   * fix, and retrying the same input verbatim will not help. A failed one is a genuine error.
   *
   * `reauth` is a third thing again, and the one the user can actually do something about: the firm's
   * PMS connection has lapsed, so nothing about this matter or this input is wrong and "Try again"
   * cannot help until someone reconnects. It carries no `message` because none of the failure's own
   * text is worth showing — the copy is ours, and it names the provider and where to go.
   */
  | { phase: 'error'; kind: 'declined' | 'failed'; message: string }
  | { phase: 'error'; kind: 'reauth' };

interface ImportMatterModalProps {
  provider: PmsProvider;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function ImportMatterModal({
  provider,
  open,
  onOpenChange
}: ImportMatterModalProps) {
  const config = getPmsImportProviderConfig(provider);
  const router = useRouter();
  const [mode, setMode] = useState<PmsLookupMode['id']>(
    config.lookupModes[0].id
  );
  const [searchValue, setSearchValue] = useState('');
  const [state, setState] = useState<ImportPhase>({ phase: 'form' });
  const [options, setOptions] =
    useState<ImportOptionsValue>(EMPTY_IMPORT_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Reset user-facing state during render whenever the dialog transitions
  // closed; the polling interval is a side effect, cleaned up in the effect
  // below so we don't touch refs during render.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setState({ phase: 'form' });
      setMode(config.lookupModes[0].id);
      setSearchValue('');
      setOptions(EMPTY_IMPORT_OPTIONS);
      setIsLoading(false);
    }
  }
  useEffect(() => {
    if (!open) stopPolling();
    return stopPolling;
  }, [open, stopPolling]);

  // Auto-redirect on success
  useEffect(() => {
    if (state.phase !== 'success') return;

    const timer = setTimeout(() => {
      onOpenChange(false);
      router.push(MATTER_ROUTES.OVERVIEW(state.valeMatterId));
    }, 2000);
    return () => clearTimeout(timer);
  }, [state, router, onOpenChange]);

  const startPolling = useCallback(
    (externalMatterId: string) => {
      pollRef.current = setInterval(async () => {
        try {
          const { status, valeMatterId } = await getPmsImportSingleStatus(
            provider,
            externalMatterId
          );

          if (status === 'imported' && valeMatterId) {
            stopPolling();
            setState({ phase: 'success', valeMatterId });
          } else if (status === 'not_found') {
            stopPolling();
            setState({
              phase: 'error',
              kind: 'failed',
              message: 'Matter not found during import'
            });
          }
        } catch {
          // A transient poll failure is not an import failure — the import is already running
          // server-side. Keep polling; the interval is cleared when the dialog closes.
        }
      }, IMPORT_SINGLE_POLL_INTERVAL_MS);
    },
    [provider, stopPolling]
  );

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchValue.trim();
    if (!trimmed) return;

    setIsLoading(true);

    try {
      if (mode === 'name') {
        const matters = await lookupPmsMattersByClientName(provider, trimmed);

        if (matters.length === 0) {
          setState({
            phase: 'error',
            kind: 'declined',
            message: `No matters found for client name: ${trimmed}`
          });
          return;
        }

        setState({ phase: 'results', matters });
        return;
      }

      const matter = await lookupPmsMatterByReference(provider, trimmed);
      setState({ phase: 'preview', matter });
    } catch (error) {
      if (isIntegrationReauthRequiredError(error)) {
        setState({ phase: 'error', kind: 'reauth' });
        return;
      }
      setState({
        phase: 'error',
        kind: 'declined',
        message: errorMessage(error, 'Matter not found')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmImport = async (externalMatterId: string) => {
    setIsLoading(true);

    try {
      const result = await importSinglePmsMatter(provider, {
        externalMatterId,
        // Only ever sent for providers that need them — an empty string would fail the backend's enum
        // validation, so an unanswered optional field is omitted rather than sent blank.
        ...(options.valeMatterType
          ? { valeMatterType: options.valeMatterType }
          : {}),
        ...(options.state ? { state: options.state } : {}),
        ...(options.decedentExternalContactId
          ? { decedentExternalContactId: options.decedentExternalContactId }
          : {}),
        ...(options.decedentFirstName && options.decedentLastName
          ? {
              decedentFirstName: options.decedentFirstName,
              decedentLastName: options.decedentLastName
            }
          : {})
      });

      if (!result.success) {
        setState({
          phase: 'error',
          kind: 'declined',
          message: result.error || 'This matter could not be imported'
        });
        return;
      }

      if (result.valeMatterId) {
        setState({ phase: 'success', valeMatterId: result.valeMatterId });
        return;
      }

      setState({ phase: 'importing', externalMatterId });
      startPolling(externalMatterId);
    } catch (error) {
      if (isIntegrationReauthRequiredError(error)) {
        setState({ phase: 'error', kind: 'reauth' });
        return;
      }
      setState({
        phase: 'error',
        kind: 'failed',
        message: errorMessage(error, 'Failed to import matter')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const activeMode =
    config.lookupModes.find((lookupMode) => lookupMode.id === mode) ??
    config.lookupModes[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ProviderMark config={config} />
            <DialogTitle>Import matter from {config.label}</DialogTitle>
          </div>
        </DialogHeader>

        {state.phase === 'form' && (
          <form onSubmit={handleLookup} className="space-y-4">
            {config.lookupModes.length > 1 && (
              <Tabs
                value={mode}
                onValueChange={(value) => {
                  setMode(value as PmsLookupMode['id']);
                  setSearchValue('');
                }}
              >
                <TabsList>
                  {config.lookupModes.map((lookupMode) => (
                    <TabsTrigger key={lookupMode.id} value={lookupMode.id}>
                      {lookupMode.tabLabel}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            <div className="space-y-2">
              <Label htmlFor="matter-search">{activeMode.fieldLabel}</Label>
              <Input
                className="mt-1"
                id="matter-search"
                placeholder={activeMode.placeholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
              />
              <p className="text-muted-foreground text-xs">{activeMode.hint}</p>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={!searchValue.trim() || isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  'Search'
                )}
              </Button>
            </div>
          </form>
        )}

        {state.phase === 'results' && (
          <ResultsPhase
            matters={state.matters}
            onSelect={(matter) => setState({ phase: 'preview', matter })}
            onBack={() => setState({ phase: 'form' })}
          />
        )}

        {state.phase === 'preview' && (
          <PreviewPhase
            config={config}
            provider={provider}
            matter={state.matter}
            options={options}
            onOptionsChange={setOptions}
            isLoading={isLoading}
            onConfirm={() => handleConfirmImport(state.matter.externalMatterId)}
            onBack={() => setState({ phase: 'form' })}
          />
        )}

        {state.phase === 'importing' && (
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <Loader2 className="text-muted-foreground h-10 w-10 animate-spin" />
            </div>
            <div className="space-y-1">
              <p className="font-medium">Importing matter...</p>
              <p className="text-muted-foreground text-sm">
                This may take a moment. You can close this dialog and the import
                will continue in the background.
              </p>
            </div>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        )}

        {state.phase === 'success' && (
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <CheckCircle2 className="text-success-subtle-foreground h-10 w-10" />
            </div>
            <div className="space-y-1">
              <p className="font-medium">Matter imported successfully</p>
              <p className="text-muted-foreground text-sm">
                Redirecting to matter...
              </p>
            </div>
          </div>
        )}

        {/*
          A lapsed connection is not an import failure, so it does not get the failure treatment: no
          red alert mark and no "Try again", which would only fail again. It reads as what it is — a
          connection to restore — and points at the one place that can restore it.
        */}
        {state.phase === 'error' && state.kind === 'reauth' && (
          <Banner
            tone="caution"
            title={`Your ${config.label} connection has expired`}
            line={`Vale can’t reach ${config.label} until the connection is re-authenticated. Reconnect it on the Integrations page, then import this matter again.`}
            action={{
              label: 'Go to Integrations',
              href: ACCOUNT_ROUTES.INTEGRATIONS
            }}
          />
        )}

        {state.phase === 'error' && state.kind !== 'reauth' && (
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <AlertCircle className="text-error-subtle-foreground h-10 w-10" />
            </div>
            <div className="space-y-1">
              <p className="font-medium">
                {state.kind === 'declined'
                  ? 'This matter can’t be imported yet'
                  : 'Import failed'}
              </p>
              <p className="text-muted-foreground text-sm">{state.message}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setState({ phase: 'form' })}
            >
              Try again
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The PMS's brand mark, or a neutral icon for a provider with no asset.
 *
 * The fallback stays because `logoUrl` is optional by design: a provider whose brand mark we do not
 * have renders a neutral icon rather than a fabricated one, since inventing another company's mark
 * misrepresents them. Both current providers ship an asset.
 */
function ProviderMark({
  config,
  size = 24
}: {
  config: PmsImportProviderConfig;
  size?: number;
}) {
  if (!config.logoUrl) {
    return (
      <Building2
        className="text-muted-foreground shrink-0"
        style={{ width: size, height: size }}
        aria-hidden
      />
    );
  }

  return (
    <Image src={config.logoUrl} alt={config.label} width={size} height={size} />
  );
}

function ResultsPhase({
  matters,
  onSelect,
  onBack
}: {
  matters: MatterLookupResult[];
  onSelect: (matter: MatterLookupResult) => void;
  onBack: () => void;
}) {
  return (
    <div className="min-w-0 space-y-4">
      <p className="text-muted-foreground text-sm">
        {matters.length} matching matter{matters.length === 1 ? '' : 's'} found.
        Select one to continue.
      </p>
      <div className="max-h-80 min-w-0 space-y-2 overflow-y-auto">
        {matters.map((matter) => (
          <button
            key={matter.externalMatterId}
            type="button"
            onClick={() => onSelect(matter)}
            className="hover:bg-muted/50 flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border p-3 text-left"
          >
            <div className="min-w-0 space-y-0.5">
              <p className="truncate font-medium">{matter.title}</p>
              <p className="text-muted-foreground truncate text-xs">
                {matter.referenceNumber} · {matter.matterTypeName} ·{' '}
                {matter.status}
              </p>
            </div>
            <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
          </button>
        ))}
      </div>
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back
      </Button>
    </div>
  );
}

function PreviewPhase({
  config,
  provider,
  matter,
  options,
  onOptionsChange,
  isLoading,
  onConfirm,
  onBack
}: {
  config: PmsImportProviderConfig;
  provider: PmsProvider;
  matter: MatterLookupResult;
  options: ImportOptionsValue;
  onOptionsChange: (value: ImportOptionsValue) => void;
  isLoading: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const optionsComplete = areImportOptionsComplete(config, options);

  return (
    // The action row sits OUTSIDE the scroll region: a provider that asks for import options makes this
    // pane tall enough to scroll, and a Confirm button that scrolls away with the content leaves the
    // dialog looking like it has no primary action at all.
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="[max-height:min(55vh,32rem)] min-w-0 space-y-4 overflow-y-auto pr-1">
        <div className="min-w-0 space-y-3 rounded-lg border p-4">
          <DetailRow label="Reference" value={matter.referenceNumber} />
          <DetailRow label="Title" value={matter.title} />
          <DetailRow label="Description" value={matter.description} />
          <DetailRow label="Matter Type" value={matter.matterTypeName} />
          <DetailRow label="Status" value={matter.status} />
          {matter.openedDate && (
            <DetailRow
              label="Opened"
              value={new Date(matter.openedDate).toLocaleDateString('en-AU', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}
            />
          )}
        </div>

        {!!matter.alreadySynced && (
          <Banner
            tone="caution"
            title="This matter is already synced to Vale."
            action={
              matter.valeMatterId
                ? {
                    label: 'View matter',
                    href: MATTER_ROUTES.OVERVIEW(matter.valeMatterId)
                  }
                : undefined
            }
          />
        )}

        {config.requiresValeMatterType && !matter.alreadySynced && (
          <ImportOptions
            config={config}
            provider={provider}
            externalMatterId={matter.externalMatterId}
            value={options}
            onChange={onOptionsChange}
          />
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t pt-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onConfirm}
          disabled={matter.alreadySynced || isLoading || !optionsComplete}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            'Confirm Import'
          )}
        </Button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="min-w-0 flex-1 truncate text-right font-medium">
        {value}
      </span>
    </div>
  );
}
